import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { configService, AppConfig, BoothConfig, HourlyPackage, ContactInfo } from '../services/ConfigService';
import RiyalSymbol from '../components/RiyalSymbol';
import BackButton from '../components/BackButton';
import { BoothPricingEditor } from '../components/BoothPricingEditor';
import '../styles/AdminPanel.css';

function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState<AppConfig>(configService.getConfig());
  const [activeTab, setActiveTab] = useState<'booths' | 'pricing' | 'features' | 'contact' | 'bookings'>('booths');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Read URL hash to set active tab and reload booths from API
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['booths', 'pricing', 'features', 'contact'].includes(hash)) {
      setActiveTab(hash as any);
    }
    
    // Reload configuration from API to get latest data
    const loadLatestConfig = async () => {
      try {
        setLoading(true);
        await configService.reloadConfig();
        setConfig(configService.getConfig());
        console.log('✅ Reloaded configuration from database');
      } catch (error) {
        console.error('❌ Error reloading configuration:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLatestConfig();
  }, [location.hash]);

  // Update URL when tab changes
  const handleTabChange = (tab: 'booths' | 'pricing' | 'features' | 'contact' | 'bookings') => {
    if (tab === 'bookings') {
      navigate('/admin/bookings');
      return;
    }
    setActiveTab(tab);
    navigate(`/admin-panel#${tab}`, { replace: true });
  };

  const handleBoothUpdate = (index: number, updates: Partial<BoothConfig>) => {
    const newConfig = { ...config };
    newConfig.booths[index] = { ...newConfig.booths[index], ...updates };
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleHourlyPackageUpdate = (index: number, updates: Partial<HourlyPackage>) => {
    const newConfig = { ...config };
    newConfig.hourlyPackages[index] = { ...newConfig.hourlyPackages[index], ...updates };
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleTicketSystemUpdate = (updates: any) => {
    const newConfig = { ...config };
    newConfig.ticketSystem = { ...newConfig.ticketSystem, ...updates };
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleFeatureUpdate = (type: 'hourlyFeatures' | 'ticketFeatures', index: number, value: string) => {
    const newConfig = { ...config };
    newConfig.features[type][index] = value;
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const addFeature = (type: 'hourlyFeatures' | 'ticketFeatures') => {
    const newConfig = { ...config };
    newConfig.features[type].push('ميزة جديدة');
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const removeFeature = (type: 'hourlyFeatures' | 'ticketFeatures', index: number) => {
    const newConfig = { ...config };
    newConfig.features[type].splice(index, 1);
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleContactUpdate = (updates: Partial<ContactInfo>) => {
    const newConfig = { ...config };
    newConfig.contactInfo = { ...newConfig.contactInfo, ...updates };
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleDepositUpdate = (updates: any) => {
    const newConfig = { ...config };
    newConfig.depositSettings = { ...newConfig.depositSettings, ...updates } as any;
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      await configService.saveConfig(config);
      setHasUnsavedChanges(false);
      setSuccessMessage('تم حفظ التغييرات بنجاح في قاعدة البيانات!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving config:', error);
      setSuccessMessage('تم حفظ التغييرات محلياً، لكن فشل حفظها في قاعدة البيانات');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('هل أنت متأكد من إعادة الإعدادات إلى القيم الافتراضية؟')) {
      setIsSaving(true);
      try {
        await configService.resetToDefault();
        setConfig(configService.getConfig());
        setHasUnsavedChanges(false);
        setSuccessMessage('تم إعادة تعيين الإعدادات بنجاح في قاعدة البيانات!');
        setShowSuccess(true);
      } catch (error) {
        console.error('Error resetting config:', error);
        setSuccessMessage('فشل في إعادة تعيين الإعدادات في قاعدة البيانات');
        setShowSuccess(true);
      } finally {
        setIsSaving(false);
      }
      setTimeout(() => setShowSuccess(false), 5000);
    }
  };


  const handleLogout = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('لديك تغييرات غير محفوظة. هل أنت متأكد من تسجيل الخروج؟')) {
        return;
      }
    }
    sessionStorage.removeItem('adminAuthenticated');
    navigate('/admin-login');
  };

  return (
    <motion.div 
      className="admin-panel"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <div className="page-title">
            لوحة التحكم الإدارية
          </div>
          <div className="nav-actions">
            <BackButton 
              to="/admin/pages-management" 
              label="إدارة الصفحات" 
              className="primary small"
            />
            <Link to="/" className="back-to-site">
              العودة للموقع
            </Link>
            <button onClick={handleLogout} className="logout-btn">
              تسجيل خروج
            </button>
          </div>
        </div>
      </nav>

      <div className="admin-container">
        <div className="admin-tabs">
          <motion.button 
            className={`tab-button ${activeTab === 'booths' ? 'active' : ''}`}
            onClick={() => handleTabChange('booths')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">🏠</span>
            <span>الكابينات</span>
          </motion.button>
          <motion.button 
            className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
            onClick={() => handleTabChange('pricing')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">💰</span>
            <span>الأسعار والباقات</span>
          </motion.button>
          <motion.button 
            className={`tab-button ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => handleTabChange('features')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">✨</span>
            <span>المميزات</span>
          </motion.button>
          <motion.button 
            className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => handleTabChange('contact')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">📞</span>
            <span>معلومات الاتصال</span>
          </motion.button>
          <motion.button 
            className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => handleTabChange('bookings')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">👥</span>
            <span>العملاء المؤكدون</span>
          </motion.button>
        </div>

        <div className="admin-content">
          {loading && (
            <div className="loading-state" style={{ textAlign: 'center', padding: '50px' }}>
              <p>🔄 جاري تحميل الإعدادات من قاعدة البيانات...</p>
            </div>
          )}
          
          {!loading && activeTab === 'booths' && (
            <div className="booths-section">
              <h2>إدارة الكابينات</h2>
              <div className="section-description">
                <strong>💡 نصيحة:</strong> يمكنك تخصيص أسماء وأوصاف الكابينات لتتناسب مع احتياجاتك. المقاسات تُستخدم لحساب المساحة المطلوبة.
              </div>
              {config.booths.map((booth, index) => (
                <div key={index} className="booth-config">
                  <div className="config-header">
                    <h3>كابينة {index + 1}</h3>
                  </div>
                  
                  <div className="config-fields">
                    <div className="field-group">
                      <label>
                        اسم الكابينة
                        <div className="help-tooltip">
                          <div className="help-icon">؟</div>
                          <div className="help-content">اسم مميز للكابينة يظهر للعملاء</div>
                        </div>
                      </label>
                      <input
                        type="text"
                        value={booth.name}
                        onChange={(e) => handleBoothUpdate(index, { name: e.target.value })}
                        placeholder="مثال: كابينة الألوان المبهجة"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>
                        الوصف
                        <div className="help-tooltip">
                          <div className="help-icon">؟</div>
                          <div className="help-content">وصف تفصيلي لمحتويات الكابينة ومميزاتها</div>
                        </div>
                      </label>
                      <textarea
                        value={booth.description}
                        onChange={(e) => handleBoothUpdate(index, { description: e.target.value })}
                        rows={3}
                        placeholder="مثال: كابينة مجهزة بإضاءة احترافية وخلفيات متنوعة..."
                      />
                    </div>
                    
                    <div className="dimensions-fields">
                      <h4>المقاسات (سم)</h4>
                      <div className="field-group-row">
                        <div className="field-group">
                          <label>الارتفاع</label>
                          <input
                            type="number"
                            value={booth.dimensions?.height || ''}
                            onChange={(e) => handleBoothUpdate(index, { 
                              dimensions: {
                                ...booth.dimensions,
                                height: parseInt(e.target.value) || 0,
                                width: booth.dimensions?.width || 0,
                                depth: booth.dimensions?.depth || 0
                              }
                            })}
                            min="0"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>العرض</label>
                          <input
                            type="number"
                            value={booth.dimensions?.width || ''}
                            onChange={(e) => handleBoothUpdate(index, { 
                              dimensions: {
                                height: booth.dimensions?.height || 0,
                                width: parseInt(e.target.value) || 0,
                                depth: booth.dimensions?.depth || 0
                              }
                            })}
                            min="0"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>العمق</label>
                          <input
                            type="number"
                            value={booth.dimensions?.depth || ''}
                            onChange={(e) => handleBoothUpdate(index, { 
                              dimensions: {
                                height: booth.dimensions?.height || 0,
                                width: booth.dimensions?.width || 0,
                                depth: parseInt(e.target.value) || 0
                              }
                            })}
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <BoothPricingEditor 
                      booth={booth} 
                      onUpdate={(customPricing) => handleBoothUpdate(index, { customPricing })} 
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="pricing-section">
              <div className="section-description">
                <strong>💰 إدارة الأسعار:</strong> يمكنك تفعيل أو إلغاء أي من أنظمة التسعير. النظام المُفعّل سيظهر للعملاء في الموقع.
              </div>
              
              <div className="pricing-group">
                <div className="system-header">
                  <h2>إعدادات العربون</h2>
                </div>
                <div className="package-config">
                  <div className="config-fields horizontal">
                    <div className="field-group">
                      <label>تفعيل العربون <span style={{fontSize: '0.8em', color: '#666', fontWeight: 'normal'}}>(لا يطبق على دفع تمارا)</span></label>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={config.depositSettings?.enabled || false}
                          onChange={(e) => handleDepositUpdate({ enabled: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="field-group">
                      <label>نوع العربون</label>
                      <select
                        value={config.depositSettings?.type || 'percentage'}
                        onChange={(e) => handleDepositUpdate({ type: e.target.value as 'percentage' | 'fixed' })}
                        className="settings-select"
                      >
                        <option value="percentage">نسبة مئوية (%)</option>
                        <option value="fixed">مبلغ ثابت (ريال)</option>
                      </select>
                    </div>
                    {config.depositSettings?.type === 'fixed' ? (
                      <div className="field-group">
                        <label>مبلغ العربون (ريال)</label>
                        <input
                          type="number"
                          value={config.depositSettings?.amount || 100}
                          onChange={(e) => handleDepositUpdate({ amount: parseInt(e.target.value) || 0 })}
                          min="0"
                        />
                      </div>
                    ) : (
                      <div className="field-group">
                        <label>نسبة العربون (%)</label>
                        <input
                          type="number"
                          value={config.depositSettings?.percentage || 50}
                          onChange={(e) => handleDepositUpdate({ percentage: parseInt(e.target.value) || 0 })}
                          min="0"
                          max="100"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pricing-group">
                <div className="system-header">
                  <h2>
                    نظام الساعات
                    <span className="info-badge">حسب المدة</span>
                  </h2>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={config.hourlySystemEnabled}
                      onChange={(e) => setConfig({...config, hourlySystemEnabled: e.target.checked})}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                {config.hourlyPackages.map((pkg, index) => (
                  <div key={index} className="package-config">
                    <div className="config-header">
                      <h4>باقة {pkg.hours} ساعات</h4>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={pkg.enabled}
                          onChange={(e) => handleHourlyPackageUpdate(index, { enabled: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    
                    <div className="config-fields horizontal">
                      <div className="field-group">
                        <label>عدد الساعات</label>
                        <input
                          type="number"
                          value={pkg.hours}
                          onChange={(e) => handleHourlyPackageUpdate(index, { hours: parseInt(e.target.value) || 0 })}
                          min="1"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>
                          السعر الحالي
                          <RiyalSymbol size={16} className="label-riyal" />
                        </label>
                        <input
                          type="number"
                          value={pkg.price}
                          onChange={(e) => handleHourlyPackageUpdate(index, { price: parseInt(e.target.value) || 0 })}
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <div className="discount-section">
                      <div className="config-fields horizontal">
                        <div className="field-group">
                          <label>
                            السعر القديم (قبل الخصم)
                            <RiyalSymbol size={16} className="label-riyal" />
                          </label>
                          <input
                            type="number"
                            value={pkg.originalPrice || ''}
                            onChange={(e) => handleHourlyPackageUpdate(index, { originalPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                            min="0"
                            placeholder="اختياري"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>نسبة الخصم %</label>
                          <input
                            type="number"
                            value={pkg.discount || ''}
                            onChange={(e) => handleHourlyPackageUpdate(index, { discount: e.target.value ? parseInt(e.target.value) : undefined })}
                            min="0"
                            max="100"
                            placeholder="اختياري"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pricing-group">
                <div className="system-header">
                  <h2>
                    نظام التذاكر
                    <span className="info-badge">حسب عدد الصور</span>
                  </h2>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={config.ticketSystemEnabled}
                      onChange={(e) => setConfig({...config, ticketSystemEnabled: e.target.checked})}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="package-config">
                  <div className="config-header">
                    <h4>إعدادات نظام التذاكر</h4>
                  </div>
                  
                  <div className="config-fields">
                    <div className="field-group">
                      <label>
                        رسوم التركيب الحالية
                        <RiyalSymbol size={16} className="label-riyal" />
                      </label>
                      <input
                        type="number"
                        value={config.ticketSystem.installationFee}
                        onChange={(e) => handleTicketSystemUpdate({ installationFee: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>
                        رسوم التركيب القديمة (قبل الخصم)
                        <RiyalSymbol size={16} className="label-riyal" />
                      </label>
                      <input
                        type="number"
                        value={config.ticketSystem.originalInstallationFee || ''}
                        onChange={(e) => handleTicketSystemUpdate({ originalInstallationFee: e.target.value ? parseInt(e.target.value) : undefined })}
                        min="0"
                        placeholder="اختياري"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>نسبة خصم رسوم التركيب %</label>
                      <input
                        type="number"
                        value={config.ticketSystem.installationDiscount || ''}
                        onChange={(e) => handleTicketSystemUpdate({ installationDiscount: e.target.value ? parseInt(e.target.value) : undefined })}
                        min="0"
                        max="100"
                        placeholder="اختياري"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>عدد التذاكر المجانية</label>
                      <input
                        type="number"
                        value={config.ticketSystem.freeTickets}
                        onChange={(e) => handleTicketSystemUpdate({ freeTickets: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>
                        سعر التذكرة الإضافية الحالي
                        <RiyalSymbol size={16} className="label-riyal" />
                      </label>
                      <input
                        type="number"
                        value={config.ticketSystem.ticketPrice}
                        onChange={(e) => handleTicketSystemUpdate({ ticketPrice: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>
                        سعر التذكرة القديم (قبل الخصم)
                        <RiyalSymbol size={16} className="label-riyal" />
                      </label>
                      <input
                        type="number"
                        value={config.ticketSystem.originalTicketPrice || ''}
                        onChange={(e) => handleTicketSystemUpdate({ originalTicketPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                        min="0"
                        placeholder="اختياري"
                      />
                    </div>
                    
                    <div className="field-group">
                      <label>نسبة خصم سعر التذاكر %</label>
                      <input
                        type="number"
                        value={config.ticketSystem.ticketDiscount || ''}
                        onChange={(e) => handleTicketSystemUpdate({ ticketDiscount: e.target.value ? parseInt(e.target.value) : undefined })}
                        min="0"
                        max="100"
                        placeholder="اختياري"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="features-section">
              <div className="section-description">
                <strong>✨ المميزات:</strong> اكتب المميزات التي تقدمها لكل نظام. ستظهر هذه المميزات للعملاء عند اختيار الباقة.
              </div>
              <div className="features-group">
                <h2>مميزات نظام الساعات</h2>
                {config.features.hourlyFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureUpdate('hourlyFeatures', index, e.target.value)}
                    />
                    <button 
                      className="remove-btn"
                      onClick={() => removeFeature('hourlyFeatures', index)}
                    >
                      حذف
                    </button>
                  </div>
                ))}
                <button 
                  className="add-feature-btn"
                  onClick={() => addFeature('hourlyFeatures')}
                >
                  + إضافة ميزة
                </button>
              </div>

              <div className="features-group">
                <h2>مميزات نظام التذاكر</h2>
                {config.features.ticketFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureUpdate('ticketFeatures', index, e.target.value)}
                    />
                    <button 
                      className="remove-btn"
                      onClick={() => removeFeature('ticketFeatures', index)}
                    >
                      حذف
                    </button>
                  </div>
                ))}
                <button 
                  className="add-feature-btn"
                  onClick={() => addFeature('ticketFeatures')}
                >
                  + إضافة ميزة
                </button>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="contact-section">
              <h2>معلومات الاتصال</h2>
              <div className="section-description">
                <strong>📞 التواصل:</strong> هذه المعلومات ستظهر في أسفل الموقع وفي رسائل البريد الإلكتروني المرسلة للعملاء.
              </div>
              <div className="contact-config">
                <div className="config-fields">
                  <div className="field-group">
                    <label>📧 البريد الإلكتروني</label>
                    <input
                      type="email"
                      value={config.contactInfo?.email || ''}
                      onChange={(e) => handleContactUpdate({ email: e.target.value })}
                      placeholder="info@example.com"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>📱 رقم الواتساب</label>
                    <input
                      type="text"
                      value={config.contactInfo?.whatsapp || ''}
                      onChange={(e) => handleContactUpdate({ whatsapp: e.target.value })}
                      placeholder="+966501234567"
                      dir="ltr"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>📸 حساب الانستقرام</label>
                    <input
                      type="text"
                      value={config.contactInfo?.instagram || ''}
                      onChange={(e) => handleContactUpdate({ instagram: e.target.value })}
                      placeholder="@username"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="admin-actions">
          <motion.button 
            className={`save-btn ${hasUnsavedChanges ? 'has-changes' : ''} ${isSaving ? 'saving' : ''}`}
            onClick={handleSave}
            disabled={isSaving}
            whileHover={!isSaving ? { scale: 1.02 } : {}}
            whileTap={!isSaving ? { scale: 0.98 } : {}}
          >
            <span className="btn-content">
              {isSaving && (
                <motion.div 
                  className="spinner"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  ⚙️
                </motion.div>
              )}
              <span className="btn-text">
                {isSaving ? 'جاري الحفظ...' : hasUnsavedChanges ? '💾 حفظ التغييرات' : '✅ محفوظ'}
              </span>
            </span>
          </motion.button>
          <motion.button 
            className="reset-btn"
            onClick={handleReset}
            disabled={isSaving}
            whileHover={!isSaving ? { scale: 1.02 } : {}}
            whileTap={!isSaving ? { scale: 0.98 } : {}}
          >
            <span className="btn-content">
              <span className="btn-text">🔄 إعادة تعيين الإعدادات</span>
            </span>
          </motion.button>
        </div>

        <AnimatePresence>
          {showSuccess && (
            <motion.div 
              className="success-message"
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <div className="success-content">
                <span className="success-icon">✅</span>
                <span className="success-text">{successMessage}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {hasUnsavedChanges && (
          <motion.div 
            className="unsaved-warning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className="warning-icon">⚠️</span>
            <span>لديك تغييرات غير محفوظة</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default AdminPanel;